import { ReactNode, useCallback, useState } from "react";
import { Alert, Button, Card, Collapse, ProgressBar } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { User } from "../../../../../services/UsersService";
import {
  loadAggregation,
  QueryParamFinderState,
  queryParamFinderState,
} from "./QueryParamFinderSlice";
import QueryParamResults from "./QueryParamResults";
import { useSimulatedAggregationProgress } from "./useAggregationProgress";

export type QueryParamFinderProps = {
  readonly user: User;
};

const AggregationProgress = () => (
  <div>
    <ProgressBar now={useSimulatedAggregationProgress()} animated label="Aggregating" />
    <p className="text-muted mt-2">
      Aggregating query parameters — this can take a few minutes for large accounts.
    </p>
  </div>
);

const AggregationIdle = ({ onCheck }: { readonly onCheck: () => void }) => (
  <div>
    <p className="text-muted">
      Aggregating query parameters scans this account's cached URLs and can be expensive for large accounts.
    </p>
    <Button variant="primary" onClick={onCheck}>
      Check query parameters
    </Button>
  </div>
);

const AggregationFailure = ({ message, onRetry }: { readonly message: string; readonly onRetry: () => void }) => (
  <Alert variant="danger" className="d-flex justify-content-between align-items-center">
    <span>{message}</span>
    <Button variant="outline-danger" size="sm" onClick={onRetry}>
      Retry
    </Button>
  </Alert>
);

type AggregationBodyProps = {
  readonly userId: number;
  readonly state: QueryParamFinderState;
  readonly onLoad: () => void;
};

const AggregationBody = ({ userId, state, onLoad }: AggregationBodyProps) => {
  const current = state.userId === userId;
  return (
    <>
      {current && state.aggregationError && <AggregationFailure message={state.aggregationError} onRetry={onLoad} />}
      {!current && <AggregationIdle onCheck={onLoad} />}
      {current && state.aggregationLoading && <AggregationProgress />}
      {current && state.aggregation && !state.aggregationLoading && (
        <QueryParamResults key={userId} userId={userId} aggregation={state.aggregation} urlState={state} />
      )}
    </>
  );
};

const BODY_ID = "query-param-finder-body";

type FinderHeaderProps = { readonly open: boolean; readonly onToggle: () => void };

const FinderHeader = ({ open, onToggle }: FinderHeaderProps) => (
  <Card.Header className="d-flex justify-content-between align-items-center">
    <h3 className="h5 mb-0">Query-Param Finder</h3>
    <Button variant="outline-secondary" size="sm" aria-expanded={open} aria-controls={BODY_ID} onClick={onToggle}>
      {open ? "Hide" : "Show"}
    </Button>
  </Card.Header>
);

const CollapsiblePanel = ({ children }: { readonly children: ReactNode }) => {
  const [open, setOpen] = useState(true);
  return (
    <Card className="mb-3">
      <FinderHeader open={open} onToggle={() => setOpen(!open)} />
      <Collapse in={open}>
        <div id={BODY_ID} role="region" aria-label="Query-Param Finder">
          <Card.Body>{children}</Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

const QueryParamFinder = ({ user }: QueryParamFinderProps) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(queryParamFinderState);
  const userId = user.id;
  const load = useCallback(() => dispatch(loadAggregation({ userId })), [dispatch, userId]);

  // ponytail: the aggregation scans the account's whole cached_urls table — keep it behind the button.
  return (
    <CollapsiblePanel>
      <AggregationBody userId={userId} state={state} onLoad={load} />
    </CollapsiblePanel>
  );
};

export default QueryParamFinder;
